import { useMemo, useSyncExternalStore } from 'react';

import { type IPeerThreadSummary } from '../interface/peer-chat';
import { peerUnreadSignature, subscribeThreadStates } from '../lib/chat-thread-store';
import { aggregateUnread, type IChatUnread } from '../lib/chat-unread';

export type { IChatUnread } from '../lib/chat-unread';

/**
 * The FAB's number and its tap target, from the support engine plus the peer inbox.
 *
 * <p>Reads the live engine store so a peer thread whose engine is mounted reports its own
 * count instead of the last poll's. The snapshot is a STRING of the per-thread counts, not the
 * store's version: hosts publish on every change, and a reader keyed on the version would
 * re-render the layout, re-render the hosts, and loop. Equal counts give an equal string, so
 * `useSyncExternalStore` sees nothing to do.</p>
 */
export const useChatUnread = (
	supportUnread: number,
	peerThreads: IPeerThreadSummary[],
	me: number,
	isParent: boolean
): IChatUnread => {
	const signature = useSyncExternalStore(subscribeThreadStates, peerUnreadSignature, peerUnreadSignature);

	return useMemo(() => {
		const liveUnread = new Map<string, number>();
		for (const part of signature ? signature.split('|') : []) {
			const at = part.lastIndexOf('=');
			if (at > 0) liveUnread.set(part.slice(0, at), Number(part.slice(at + 1)) || 0);
		}
		return aggregateUnread({ supportUnread, peerThreads, me, liveUnread, isParent });
	}, [supportUnread, peerThreads, me, isParent, signature]);
};
